/**
 * @file receipt-item-format.ts
 * @description Тексты строки приёмки: партия и срок (phase19.31, Б15; SC59).
 *
 * Замечание пользователя на RCP-2026-000021 (черновик): срок внесён, а в
 * таблице его не видно. У строки черновика номера партии ещё нет (партию
 * сервер заводит при проведении), и общая подпись «партия · до» при пустом
 * номере срок теряла. Теперь партия и срок — две колонки, а у черновика без
 * номера партия подписана «присвоит система» (партия будет — это не «без
 * партии»).
 *
 * Чистые функции — их же читают таблица (`≥xl` в черновике, `≥lg` в
 * проведённой) и карточки, и тесты.
 */

import { formatLotDate, lotLabel } from "./document-texts";
import type { GoodsReceiptItemDto } from "../../lib/api-wms";

/** Подпись партии черновика без номера: номер присвоит сервер при проведении. */
export const RECEIPT_LOT_PENDING = "присвоит система";

/** Номер партии строки: из заведённой партии, потом введённый в строке. */
export function receiptBatchNumber(item: GoodsReceiptItemDto): string | null {
  return item.lot?.batchNumber || item.batchNumber || null;
}

/** Срок строки: у заведённой партии — её срок, у черновика — срок строки. */
export function receiptExpiresAt(item: GoodsReceiptItemDto): string | null {
  return item.lot?.expiresAt ?? item.expiresAt ?? null;
}

/** Колонка «Партия»: номер либо «присвоит система» / «без партии» вторым цветом. */
export function receiptLotView(item: GoodsReceiptItemDto, draft: boolean): { text: string; muted: boolean } {
  const number = receiptBatchNumber(item);
  if (number) return { text: number, muted: false };
  return { text: draft ? RECEIPT_LOT_PENDING : "без партии", muted: true };
}

/**
 * Колонка «Годен до»: дата «дд.мм.гггг»; «не указан» янтарным, если срок
 * обязателен и пуст (сервер прислал строку в `missingExpiryItems`); «—», если
 * срок не ведётся.
 */
export function receiptExpiryView(
  item: GoodsReceiptItemDto,
  missingExpiry: boolean,
): { text: string; tone: "default" | "warning" | "muted" } {
  const expiresAt = receiptExpiresAt(item);
  if (expiresAt) return { text: formatLotDate(expiresAt), tone: "default" };
  if (missingExpiry) return { text: "не указан", tone: "warning" };
  return { text: "—", tone: "muted" };
}

/** Одна строка для карточки: «присвоит система · до 08.10.2027». */
export function receiptLotLine(item: GoodsReceiptItemDto, draft: boolean): string {
  return lotLabel(receiptBatchNumber(item), receiptExpiresAt(item), draft ? RECEIPT_LOT_PENDING : "без партии");
}

/**
 * Отдельный пункт «⋯» про срок: только у проведённой строки и только
 * «пусто → значение» (W20). У черновика срок, как и остальные поля, правится
 * в «Изменить позицию» (Б16) — отдельного пункта нет. `null` — пункта нет.
 */
export function receiptExpiryActionLabel(
  item: GoodsReceiptItemDto,
  draft: boolean,
  missingExpiry: boolean,
): "Указать срок" | null {
  if (draft) return null;
  return missingExpiry && !receiptExpiresAt(item) ? "Указать срок" : null;
}

/** Пункт «⋯» про ячейку строки черновика: «Указать» или «Сменить». */
export function receiptBinActionLabel(item: GoodsReceiptItemDto): "Указать ячейку" | "Сменить ячейку" {
  return item.bin || item.binId ? "Сменить ячейку" : "Указать ячейку";
}
